/**
 * KPI Investors — métricas financieras para super_admin.
 * Consume GET /api/admin/investor-kpi
 */

import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Info,
  Percent,
  RefreshCw,
  Repeat,
  Shield,
  ShoppingBag,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { InvestorMrrEvolutionCharts } from "@/components/admin/InvestorMrrEvolutionCharts";
import {
  formatCompactMoney,
  formatCompactNumber,
  InvestorMetricCard,
  InvestorRevenueGrowthPanel,
  InvestorSectionHeading,
} from "@/components/admin/InvestorKpiPanels";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useIsSuperAdmin } from "@/lib/auth";
import {
  combineInvestorRevenueUsd,
  formatDecimal,
  formatInvestorMoney,
  INVESTOR_ARS_PER_USD,
  normalizeInvestorKpiPayload,
  type InvestorKpiMetrics,
  type MrrEvolutionMonthRow,
  type MrrEvolutionPeriodRow,
} from "@/lib/investorKpiDisplay";
import { cn } from "@/lib/utils";

function NorthStarSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div
          key={i}
          className="glass-card animate-pulse rounded-xl border-l-4 border-l-white/10 p-4"
        >
          <div className="mb-4 flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-white/[0.08]" />
            <div className="space-y-2">
              <div className="h-3 w-24 rounded bg-white/[0.08]" />
              <div className="h-2 w-32 rounded bg-white/[0.05]" />
            </div>
          </div>
          <div className="h-8 w-28 rounded bg-white/[0.08]" />
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="glass-card animate-pulse rounded-xl p-6">
      <div className="mb-4 h-4 w-48 rounded bg-white/[0.08]" />
      <div className="h-[280px] rounded-lg bg-white/[0.04]" />
    </div>
  );
}

function SectionDivider() {
  return <div className="border-t border-white/[0.06]" aria-hidden />;
}

export default function InvestorKpiAdmin() {
  const isSuperAdmin = useIsSuperAdmin();
  const { getAuthHeaders, isLoaded } = useAdminAuth();

  const {
    data: kpiMetrics,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["admin", "investor-kpi"],
    enabled: isLoaded && isSuperAdmin,
    queryFn: async (): Promise<InvestorKpiMetrics> => {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/investor-kpi", { headers });
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          body && typeof body === "object" && "error" in body
            ? String((body as { error: unknown }).error)
            : `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const normalized = normalizeInvestorKpiPayload(body);
      if (!normalized) throw new Error("Respuesta KPI inválida");
      return normalized;
    },
    staleTime: 60_000,
  });

  if (!isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const mrr = kpiMetrics?.mrr as Record<string, unknown> | null | undefined;
  const byTier = (mrr?.by_tier ?? {}) as Record<string, number>;
  const topSpenders = kpiMetrics?.top_spenders ?? [];

  return (
    <div className="admin-page mx-auto max-w-6xl space-y-8 pb-12">
      <AdminPageHeader
        className="border-white/[0.08]"
        description="MRR / churn / North Star para reporting de inversores. Cash day-to-day → Revenue."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="h-7 gap-1 border-amber-500/30 bg-amber-500/10 text-[11px] font-medium text-amber-200"
            >
              <Shield className="h-3 w-3" />
              Super admin
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-white/[0.12] bg-white/[0.04] hover:bg-white/[0.08]"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
              {isFetching ? "Actualizando…" : "Actualizar"}
            </Button>
          </div>
        }
      />

      {isLoading && (
        <div className="space-y-8">
          <div className="glass-card h-24 animate-pulse rounded-xl" />
          <NorthStarSkeleton />
          <ChartSkeleton />
        </div>
      )}

      {isError && (
        <Alert variant="destructive" className="glass-card border-destructive/40">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error al cargar KPIs</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Error desconocido"}
          </AlertDescription>
        </Alert>
      )}

      {kpiMetrics && (
        <div className="space-y-10">
          <Alert className="glass-card border-primary/25 bg-primary/5">
            <Target className="h-4 w-4 text-primary" />
            <AlertTitle className="text-sm font-semibold">anaafernandez · tracking comercial</AlertTitle>
            <AlertDescription className="text-xs leading-relaxed text-muted-foreground">
              Revenue North Star desde{" "}
              <strong className="text-foreground">{kpiMetrics.tracking_window.start_date}</strong>{" "}
              (<code className="rounded bg-white/[0.06] px-1 py-0.5 text-[10px]">stripe_transactions</code> scope
              token + <code className="rounded bg-white/[0.06] px-1 py-0.5 text-[10px]">manual_transactions.source</code>
              ). MRR run-rate filtrado por marca; churn por primer cobro recurrente.
            </AlertDescription>
            {kpiMetrics.api_data && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {kpiMetrics.api_data.reference_only && (
                  <Badge variant="secondary" className="font-normal text-[10px]">
                    API referencia sin filtro marca
                  </Badge>
                )}
                {kpiMetrics.api_data.stripe && (
                  <Badge variant="outline" className="gap-1 border-blue-500/30 bg-blue-500/10 font-normal">
                    <CheckCircle className="h-3 w-3 text-blue-400" />
                    Stripe API
                  </Badge>
                )}
                {kpiMetrics.api_data.mercadopago && (
                  <Badge variant="outline" className="gap-1 border-emerald-500/30 bg-emerald-500/10 font-normal">
                    <CheckCircle className="h-3 w-3 text-emerald-400" />
                    MercadoPago API
                  </Badge>
                )}
                {kpiMetrics.api_data.synced_at && (
                  <span className="text-muted-foreground">
                    Sync:{" "}
                    {new Date(kpiMetrics.api_data.synced_at).toLocaleString("es-AR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                )}
              </div>
            )}
          </Alert>

          {/* North Star */}
          <section className="space-y-4">
            <InvestorSectionHeading
              icon={Zap}
              title="Métricas North Star"
              description="Acumulado en la ventana de tracking comercial"
            >
              <Badge
                variant="outline"
                className="border-white/[0.1] bg-white/[0.03] text-xs font-normal tabular-nums"
              >
                <Clock className="mr-1 h-3 w-3" />
                {kpiMetrics.tracking_window.start_date}
                {" → "}
                {kpiMetrics.tracking_window.end_date}
                {kpiMetrics.tracking_window.days ? ` · ${kpiMetrics.tracking_window.days} días` : ""}
              </Badge>
            </InvestorSectionHeading>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <InvestorMetricCard
                accent="green"
                icon={DollarSign}
                title="Revenue total"
                subtitle="Stripe USD + MercadoPago ARS"
              >
                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">USD</p>
                    <p className="font-display text-2xl font-bold leading-none tabular-nums text-foreground">
                      {formatInvestorMoney(kpiMetrics.revenue_metrics.total_revenue_usd, "USD")}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">ARS</p>
                    <p className="font-display text-lg font-bold leading-none tabular-nums">
                      {formatInvestorMoney(kpiMetrics.revenue_metrics.total_revenue_ars, "ARS")}
                    </p>
                  </div>
                  <div className="rounded-md border border-violet-500/25 bg-violet-500/10 px-2 py-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-violet-400">Total USD equiv.</p>
                    <p className="font-display text-lg font-bold leading-none tabular-nums text-violet-300">
                      {formatInvestorMoney(
                        kpiMetrics.revenue_metrics.total_revenue_combined_usd ??
                          combineInvestorRevenueUsd(
                            kpiMetrics.revenue_metrics.total_revenue_usd,
                            kpiMetrics.revenue_metrics.total_revenue_ars,
                          ),
                        "USD",
                      )}
                    </p>
                    <p className="mt-0.5 text-[9px] text-muted-foreground">
                      ARS ÷ {kpiMetrics.exchange_rate_ars_per_usd ?? INVESTOR_ARS_PER_USD}
                    </p>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {formatCompactNumber(kpiMetrics.revenue_metrics.total_transactions)} transacciones
                  </p>
                </div>
              </InvestorMetricCard>

              <InvestorMetricCard accent="blue" icon={Users} title="Usuarios pagadores" subtitle="≥1 pago en tracking">
                <p className="font-display text-3xl font-bold leading-none tabular-nums">
                  {formatCompactNumber(kpiMetrics.revenue_metrics.paying_users)}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {kpiMetrics.conversion_funnel.overall_conversion_rate}% conversión global
                </p>
                <p className="text-[11px] text-muted-foreground">
                  sobre {formatCompactNumber(kpiMetrics.conversion_funnel.total_registered)} registrados
                </p>
              </InvestorMetricCard>

              <InvestorMetricCard accent="purple" icon={ShoppingBag} title="ARPPU" subtitle="Revenue / pagador">
                <p className="font-display text-3xl font-bold leading-none tabular-nums">
                  {formatInvestorMoney(kpiMetrics.revenue_metrics.arppu, "USD")}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {formatDecimal(kpiMetrics.revenue_metrics.avg_transactions_per_user, 1)} tx / usuario
                </p>
              </InvestorMetricCard>

              <InvestorMetricCard accent="orange" icon={TrendingUp} title="LTV estimado" subtitle="Histórico completo">
                <p className="font-display text-3xl font-bold leading-none tabular-nums">
                  {formatInvestorMoney(kpiMetrics.revenue_metrics.estimated_ltv, "USD")}
                </p>
                <Badge variant="outline" className="mt-1 h-5 border-white/10 text-[10px] font-normal">
                  {formatCompactNumber(
                    kpiMetrics.revenue_metrics.paying_users_lifetime ??
                      kpiMetrics.revenue_metrics.paying_users,
                  )}{" "}
                  pagadores lifetime
                </Badge>
              </InvestorMetricCard>
            </div>
          </section>

          <SectionDivider />

          {/* Growth */}
          <section className="space-y-4">
            <InvestorSectionHeading
              icon={TrendingUp}
              title="Crecimiento de revenue"
              description="Períodos comerciales P1–P5, semanas y volumen diario"
            >
              <Badge variant="outline" className="border-white/[0.1] text-xs font-normal">
                <Calendar className="mr-1 h-3 w-3" />
                {kpiMetrics.tracking_window.label}
              </Badge>
            </InvestorSectionHeading>
            <InvestorRevenueGrowthPanel
              reportingPeriodRevenue={kpiMetrics.reporting_period_revenue ?? []}
              weeklyRevenue={kpiMetrics.weekly_revenue ?? []}
              dailyVolume={kpiMetrics.daily_volume ?? []}
              subscriptionChurnByPeriod={kpiMetrics.subscription_churn_by_period ?? []}
              trackingLabel={kpiMetrics.tracking_window.label}
              exchangeRate={kpiMetrics.exchange_rate_ars_per_usd ?? INVESTOR_ARS_PER_USD}
              trackingTotals={{
                total_revenue_usd: kpiMetrics.revenue_metrics.total_revenue_usd,
                total_revenue_ars: kpiMetrics.revenue_metrics.total_revenue_ars,
                total_revenue_combined_usd: kpiMetrics.revenue_metrics.total_revenue_combined_usd,
                total_transactions: kpiMetrics.revenue_metrics.total_transactions,
              }}
            />
          </section>

          <SectionDivider />

          {/* MRR */}
          <section className="space-y-4">
            <InvestorSectionHeading
              icon={Repeat}
              title="MRR · Ingresos recurrentes"
              description="Run-rate desde APIs · cobrado recurrente en el mes"
            />

            {(mrr?.active_subscriptions ?? 0) === 0 ? (
              <Alert className="glass-card border-amber-500/40 bg-amber-500/5">
                <Info className="h-4 w-4 text-amber-500" />
                <AlertTitle className="text-sm font-semibold">Sin suscripciones activas</AlertTitle>
                <AlertDescription className="text-xs">
                  No hay suscripciones recurrentes activas en Stripe o MercadoPago en este momento.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <InvestorMrrEvolutionCharts
                  totalCombinedUsd={Number(
                    mrr?.total_combined_usd ??
                      mrr?.estimated_mrr_combined_usd ??
                      combineInvestorRevenueUsd(mrr?.estimated_mrr_usd, mrr?.estimated_mrr_ars),
                  )}
                  collectedMtdUsd={Number(mrr?.collected_recurring_mtd_usd ?? 0)}
                  stripeMrrUsd={Number(mrr?.estimated_mrr_usd ?? 0)}
                  mrrArs={Number(mrr?.estimated_mrr_ars ?? 0)}
                  exchangeRate={kpiMetrics.exchange_rate_ars_per_usd ?? INVESTOR_ARS_PER_USD}
                  activeSubscriptions={Number(mrr?.active_subscriptions ?? 0)}
                  evolutionMonthly={(mrr?.mrr_evolution_monthly ?? []) as MrrEvolutionMonthRow[]}
                  evolutionByPeriod={(mrr?.mrr_evolution_by_period ?? []) as MrrEvolutionPeriodRow[]}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Card className="glass-card border-cyan-500/30">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Repeat className="h-4 w-4" />
                        Suscripciones activas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold tabular-nums">{Number(mrr?.active_subscriptions ?? 0)}</p>
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className="text-emerald-400">{Number(mrr?.mercadopago_subscriptions ?? 0)} MP</span>
                        <span className="text-muted-foreground">+</span>
                        <span className="text-blue-400">{Number(mrr?.stripe_subscriptions ?? 0)} Stripe</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-cyan-500/30">
                    <CardContent className="pt-6">
                      <p className="text-xs font-medium text-muted-foreground">MRR Stripe (USD)</p>
                      <p className="text-3xl font-bold tabular-nums text-cyan-400">
                        {formatCompactMoney(mrr?.estimated_mrr_usd ?? 0, "USD")}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {mrr?.stripe_mrr_source === "stripe_api"
                          ? "API Stripe · scope anaafernandez"
                          : "Estimación catálogo"}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-cyan-500/30">
                    <CardContent className="pt-6">
                      <p className="text-xs font-medium text-muted-foreground">MRR MercadoPago (ARS)</p>
                      <p className="text-3xl font-bold tabular-nums text-cyan-400">
                        {formatCompactMoney(mrr?.estimated_mrr_ars ?? 0, "ARS")}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {mrr?.mercadopago_mrr_source === "mercadopago_api"
                          ? "API MP · preapproval authorized"
                          : "Estimación catálogo"}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Card className="glass-card border-blue-500/20 bg-blue-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-blue-400">Standard</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold tabular-nums">{byTier.standard ?? 0}</p>
                      <p className="mt-1 text-xs text-muted-foreground">activos en BD</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-purple-500/20 bg-purple-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-purple-400">VIP</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold tabular-nums">{byTier.vip ?? 0}</p>
                      <p className="mt-1 text-xs text-muted-foreground">activos en BD</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-violet-500/20 bg-violet-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-violet-400">Platinum</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold tabular-nums">{byTier.platinum ?? 0}</p>
                      <p className="mt-1 text-xs text-muted-foreground">activos en BD</p>
                    </CardContent>
                  </Card>
                </div>

                {Number(mrr?.total_subscriptions_ever ?? 0) > 0 && (
                  <Card className="glass-card border-amber-500/25 bg-amber-500/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-sm font-medium">
                        <Activity className="h-4 w-4 text-amber-500" />
                        Salud de suscripciones
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4">
                        <div>
                          <p className="text-lg font-bold tabular-nums text-emerald-400">
                            {Number(mrr?.active_subscriptions ?? 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">Activas</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-amber-400">
                            {Number(mrr?.paused_subscriptions ?? 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">Pausadas</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-rose-400">
                            {Number(mrr?.cancelled_subscriptions ?? 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">Canceladas</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-primary">
                            {Number(mrr?.churn_rate ?? 0)}%
                          </p>
                          <p className="text-xs text-muted-foreground">Churn histórico</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </section>

          <SectionDivider />

          {/* Conversion funnel */}
          <section className="space-y-4">
            <InvestorSectionHeading
              icon={Percent}
              title="Embudo de conversión"
              description="De registro a primer pago"
            />
            <Card className="glass-card border-border/50">
              <CardContent className="grid gap-6 pt-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.04] p-4 ring-1 ring-inset ring-white/[0.06]">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Registrados</p>
                      <p className="text-3xl font-bold tabular-nums">
                        {kpiMetrics.conversion_funnel.total_registered}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-white/10" />
                  </div>
                  <div className="flex justify-center">
                    <TrendingDown className="h-5 w-5 animate-pulse text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                    <div>
                      <p className="text-sm font-medium text-emerald-400">Pagaron (ever)</p>
                      <p className="text-3xl font-bold tabular-nums text-emerald-300">
                        {kpiMetrics.conversion_funnel.users_who_paid}
                      </p>
                    </div>
                    <ShoppingBag className="h-10 w-10 text-emerald-500/20" />
                  </div>
                  <div className="rounded-lg bg-primary/10 p-4 text-center ring-1 ring-inset ring-primary/20">
                    <p className="text-sm text-muted-foreground">Conversión global</p>
                    <p className="text-4xl font-bold tabular-nums text-primary">
                      {kpiMetrics.conversion_funnel.overall_conversion_rate}%
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg bg-white/[0.04] p-4 ring-1 ring-inset ring-white/[0.06]">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nuevos (30d)</p>
                      <p className="text-3xl font-bold tabular-nums">
                        {kpiMetrics.conversion_funnel.new_users_30d}
                      </p>
                    </div>
                    <Sparkles className="h-10 w-10 text-white/10" />
                  </div>
                  <div className="flex justify-center">
                    <TrendingDown className="h-5 w-5 animate-pulse text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
                    <div>
                      <p className="text-sm font-medium text-blue-400">Nuevos que pagaron</p>
                      <p className="text-3xl font-bold tabular-nums text-blue-300">
                        {kpiMetrics.conversion_funnel.new_users_who_paid}
                      </p>
                    </div>
                    <DollarSign className="h-10 w-10 text-blue-500/20" />
                  </div>
                  <div className="rounded-lg bg-violet-500/10 p-4 text-center ring-1 ring-inset ring-violet-500/20">
                    <p className="text-sm text-muted-foreground">Conversión nuevos (30d)</p>
                    <p className="text-4xl font-bold tabular-nums text-violet-300">
                      {kpiMetrics.conversion_funnel.new_user_conversion_rate}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {Array.isArray(kpiMetrics.top_transactions) && kpiMetrics.top_transactions.length > 0 && (
            <>
              <SectionDivider />
              <section className="space-y-4">
                <InvestorSectionHeading
                  icon={DollarSign}
                  title="Top transacciones"
                  description="Mayores pagos individuales en la ventana de tracking"
                />
                <div className="glass-card investor-kpi-table-wrap">
                  <table className="investor-kpi-table">
                    <thead>
                      <tr>
                        <th className="text-left">Fecha</th>
                        <th className="text-left">Email</th>
                        <th className="text-left">Fuente</th>
                        <th className="text-right">USD equiv.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(kpiMetrics.top_transactions as Array<Record<string, unknown>>)
                        .slice(0, 10)
                        .map((t, idx) => (
                          <tr key={`${t.paid_at}-${idx}`}>
                            <td className="text-xs text-muted-foreground tabular-nums">
                              {String(t.paid_at ?? "").slice(0, 10)}
                            </td>
                            <td className="max-w-[180px] truncate text-xs">{String(t.email ?? "")}</td>
                            <td className="text-xs text-muted-foreground">{String(t.source ?? "")}</td>
                            <td className="text-right font-semibold tabular-nums">
                              {formatInvestorMoney(t.amount_usd_equivalent, "USD")}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {topSpenders.length > 0 && (
            <>
              <SectionDivider />
              <section className="space-y-4">
                <InvestorSectionHeading
                  icon={Users}
                  title="Top spenders"
                  description="Mayor revenue acumulado (USD equivalente)"
                />
                <div className="glass-card investor-kpi-table-wrap">
                  <table className="investor-kpi-table">
                    <thead>
                      <tr>
                        <th className="text-left">#</th>
                        <th className="text-left">Email</th>
                        <th className="text-right">USD equiv.</th>
                        <th className="text-right">Txs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSpenders.slice(0, 10).map((s, idx) => (
                        <tr key={s.email}>
                          <td className="text-xs tabular-nums text-muted-foreground">{idx + 1}</td>
                          <td className="max-w-[200px] truncate text-xs font-medium sm:max-w-none">{s.email}</td>
                          <td className="text-right font-semibold tabular-nums">
                            {formatInvestorMoney(s.total_usd_equivalent, "USD")}
                          </td>
                          <td className="text-right tabular-nums text-muted-foreground">
                            {s.transaction_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
}